import { Button, Divider, Icon, ListItemIcon, makeStyles, Menu, MenuItem, Theme, useTheme } from '@material-ui/core';
import { capitalizeAll, GenericMessageBox, FontIcon, useDropdown, useModePanelStyle } from '@moderepo/mode-app-components';
import { fetchAgentProjectById, ritzDataContext, selectLoggedInAgent, selectProjectAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { modeUIContext, showErrorNotification, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ReactGridLayout, { Layout } from 'react-grid-layout';
import { fetchHomeKVStore, selectHomeKVStore, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { Agent, AgentRole } from '@moderepo/mode-internal-apis';
import { useRitzTranslation } from '../../utils/customHooks';
import { DashboardPreviewCompOptions, DashboardWidgetPreviewCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';
import { DashboardWidgetContainer } from './DashboardWidgetContainer';
import { Dashboard, DashboardWidgetType, DASHBOARD_COLS, DASHBOARD_ROW_HEIGHT, DASHBOARD_WIDGETS_SPACING, DASHBOARD_WIDGET_MIN_HEIGHT,
    DASHBOARD_WIDGET_MIN_WIDTH, HOME_DASHBOARDS_KV_KEY, isDashboardWidgetSettings,
    isHomeDashboardsKeyValuePair, isProjectDashboardsSettings, ResponsiveGridLayout } from './dashboardTypes';
import { DashboardLogicApiCallResponse, moveHomeDashboardBackward, moveHomeDashboardForward, moveProjectDashboardBackward,
    moveProjectDashboardForward } from './dashboardLogic';
import { SelectDashboardWidgetTypeDialog, SelectDashboardWidgetTypeDialogProps } from './SelectDashboardWidgetTypeDialog';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';
import { getWidgetsLayout, handleWidgetResizedOrMoved } from './dashboardUtils';
import { isAlertingFeatureEnabled, isEntitiesFeatureEnabled } from '../../utils/utils';



const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            width   : '100%',
            position: 'relative',
        },

        header: {
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'space-between',
            paddingBottom : theme.spacing(1),
        },

        titleContainer: {
            flex       : 1,
            width      : '100%',
            marginRight: theme.spacing(1),
        },

        title: {
            fontSize     : 12,
            fontWeight   : 500,
            textTransform: 'uppercase',
            color        : theme.palette.text.secondary,
        },

        subtitle: {
            fontSize  : 24,
            fontWeight: 500,
            color     : theme.palette.text.primary,
        },

        [theme.breakpoints.up('sm')]: {
            header: {
                paddingBottom: theme.spacing(1.5),
            },
            titleContainer: {
                marginRight: theme.spacing(1.5),
            },
            title: {
                fontSize: 14,
            },
            subtitle: {
                fontSize: 26,
            },
        },

        [theme.breakpoints.up('md')]: {
            header: {
                paddingBottom: theme.spacing(2),
            },
            titleContainer: {
                marginRight: theme.spacing(2),
            },
            title: {
                fontSize: 14,
            },
            subtitle: {
                fontSize: 26,
            },
        },

        dropdownMenu: {
        },

        menuItem: {
            '& .icon': {
                '&.delete': {
                    color: theme.palette.error.main,
                },
            },
        },

        dashboardWidgetsContainer: {
            width: '100%',
        },

        widget: {
            height   : '100%',
            minHeight: 'auto',
            overflow : 'hidden',
        },
    };
}, { name: 'DashboardContainer' });



export interface DashboardContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly dashboardId: number;

    /**
     * The context of which page the user is on. Because this component can be shown in different pages, on a Project page where there is no
     * user or home context or on a User's Home page which has user and home context. This data is not required but it helps create a better
     * URL when we take the user to another page.
     */
    readonly routeContext?: {
        readonly userId?: number | undefined;
        readonly homeId?: number | undefined;
    }
}


/**
 * This is a container which contains Dashboard Widgets. This container will be using react-grid-layout component to layout the widget. This
 * components allows the user to use drag and drop to rearrange the widgets.
 * This container represent ONE dashboard
 */
export const DashboardContainer: React.FC<DashboardContainerProps> = (props: DashboardContainerProps) => {

    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    // Get the logged in Agent data for the given project. This is is not the same as loggedInAgent data.
    const projectAgent = loggedInAgent ? selectProjectAgent(dataState, project?.id, loggedInAgent.id) : undefined;
    const canEditDashboard = projectAgent?.role === AgentRole.ADMIN;

    // Check if entities and alerting features enabled. We need these feature to enable the Alerts dashboard widget
    const entityFeatureEnabled = isEntitiesFeatureEnabled(project, loggedInAgent);
    const alertingFeatureEnabled = isAlertingFeatureEnabled(project, loggedInAgent);

    // Dropdown menu for the Dashboard options e.g. Create Widget, Move Widgets, Delete Dashboard, etc...
    const [menuAnchorEl, isMenuOpened, onAnchorElClicked, onMenuClosed] = useDropdown();


    // Select all the dashboards for the Home/Project from the data state
    const dashboards = useMemo(() => {
        if (props.routeContext?.homeId) {
            // Get dashboards settings from Home KV
            const dashboardKV = selectHomeKVStore(appDataState, props.routeContext.homeId, HOME_DASHBOARDS_KV_KEY);
            if (isHomeDashboardsKeyValuePair(dashboardKV)) {
                return dashboardKV.value;
            }
        } else if (project && isProjectDashboardsSettings(project.supportToolSettings.dashboardSettings)) {
            // Get dashboards settings from Project settings
            return project.supportToolSettings.dashboardSettings.dashboards;
        }

        return [];
    }, [appDataState, project, props.routeContext]);


    // Select the dashboard object that this Dashboard container is being used for
    const selectedDashboard = useMemo(() => {
        if (dashboards) {
            return dashboards.find((dashboard) => {
                return dashboard.id === props.dashboardId;
            });
        }
        return undefined;
    }, [dashboards, props.dashboardId]);


    /**
     * Get the index of the selectedDashboard. We will need this index so that we can enable/disable the move dashboard buttons
     */
    const selectedDashboardIndex = useMemo(() => {
        if (dashboards && selectedDashboard) {
            return dashboards.findIndex((dashboard) => {
                return dashboard.id === selectedDashboard.id;
            });
        }
        return -1;
    }, [dashboards, selectedDashboard]);


    /**
     * A dashboard has an array of widgets but not all widgets need to be displayed in this container. Some widget might be placed inside
     * another widget, Widget Group, therefore we need to filter out widgets that are in a widget group. We will let the Widget Group handle
     * displaying these widgets
     */
    const displayableWidgets = useMemo(() => {
        if (selectedDashboard) {
            return selectedDashboard.widgets?.filter((widget) => {
                return widget.widgetGroupId === undefined;
            });
        }

        return undefined;
    }, [selectedDashboard]);


    const { trans } = useRitzTranslation(project);
    const [isInMoveAndResizeMode, setIsInMoveAndResizeMode] = useState(false);
    const classes = useStyle();
    const panelClasses = useModePanelStyle();
    const theme = useTheme();


    const [showSelectDashboardWidgetTypeDialog, setShowSelectDashboardWidgetTypeDialog] = useState<boolean>(false);


    /**
     * Create the layouts object for all the widgets based on the widgets' position and sizes. The React Grid Layout needs this object
     * to layout the widgets.
     */
    const widgetsLayouts = useMemo((): ReactGridLayout.Layouts => {
        return getWidgetsLayout(displayableWidgets, DASHBOARD_WIDGET_MIN_WIDTH, DASHBOARD_WIDGET_MIN_HEIGHT);
    }, [displayableWidgets]);



    /**
     * This useEffect is used for fetching the Home KV store which is used for storing the provided homeId's dashboard
     */
    useEffect(() => {
        if (props.routeContext?.homeId) {
            appDataDispatch(fetchHomeKVStore(props.routeContext.homeId, HOME_DASHBOARDS_KV_KEY), true, true).catch(() => {
                // ignore error because appDataDispatch already handled the error
            });
        } else {
            dataDispatch(fetchAgentProjectById(loggedInAgent.id, props.projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [appDataDispatch, dataDispatch, loggedInAgent.id, props.projectId, props.routeContext]);



    /**
     * On create dashboard clicked, we will use the slide out panel to show the UI for creating a Dashboard
     */
    const onCreateDashboardClick = useCallback(() => {
        const options: DashboardPreviewCompOptions = {
            compType : SlideOutPanelCompType.DASHBOARD_PREVIEW,
            location : SlideOutPanelLocation.RIGHT,
            compProps: {
                data: {
                    projectId: props.projectId,
                },
                routeContext: props.routeContext,
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.projectId, props.routeContext, uiDispatch]);



    /**
     * On Move Dashboard forward handler.
     */
    const onMoveDashboardForward = useCallback((dashboard: Dashboard) => {
        const onCompleted = (result: DashboardLogicApiCallResponse) => {
            if (result.errorMessageKey) {
                uiDispatch(showErrorNotification(trans(result.errorMessageKey)));
            }
        };

        if (props.routeContext?.homeId) {
            moveHomeDashboardForward(appDataState, appDataDispatch, props.routeContext.homeId, dashboard).then(onCompleted);
        } else {
            moveProjectDashboardForward(dataState, dataDispatch, props.projectId, loggedInAgent.id, dashboard).then(onCompleted);
        }

    }, [appDataDispatch, appDataState, dataDispatch, dataState, loggedInAgent.id, props.projectId, props.routeContext, trans, uiDispatch]);



    /**
     * On Move Dashboard forward handler.
     */
    const onMoveDashboardBackward = useCallback((dashboard: Dashboard) => {
        const onCompleted = (result: DashboardLogicApiCallResponse) => {
            if (result.errorMessageKey) {
                uiDispatch(showErrorNotification(trans(result.errorMessageKey)));
            }
        };

        if (props.routeContext?.homeId) {
            moveHomeDashboardBackward(appDataState, appDataDispatch, props.routeContext.homeId, dashboard).then(onCompleted);
        } else {
            moveProjectDashboardBackward(dataState, dataDispatch, props.projectId, loggedInAgent.id, dashboard).then(onCompleted);
        }

    }, [appDataDispatch, appDataState, dataDispatch, dataState, loggedInAgent.id, props.projectId, props.routeContext, trans, uiDispatch]);



    /**
     * On create widget clicked, we will use the slide out panel to show the UI for creating a widget
     */
    const onCreateWidgetClick = useCallback((widgetType: DashboardWidgetType) => {
        const options: DashboardWidgetPreviewCompOptions = {
            compType : SlideOutPanelCompType.DASHBOARD_WIDGET_PREVIEW,
            location : SlideOutPanelLocation.RIGHT,
            compProps: {
                data: {
                    projectId     : props.projectId,
                    dashboardId   : props.dashboardId,
                    selectedWidget: widgetType,
                },
                routeContext: props.routeContext,
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.routeContext, props.dashboardId, props.projectId, uiDispatch]);



    /**
     * on Widget resized or moved, call the handler to handle the event
     */
    const onWidgetResizedOrMoved = useCallback((layouts: Layout[]) => {
        handleWidgetResizedOrMoved({
            layouts,
            uiDispatch,
            dataState,
            dataDispatch,
            appDataState,
            appDataDispatch,
            loggedInAgent,
            projectId  : props.projectId,
            homeId     : props.routeContext?.homeId,
            dashboardId: props.dashboardId,
            trans,
            widgets    : displayableWidgets,
        });
    }, [appDataDispatch, appDataState, dataDispatch, dataState, displayableWidgets, loggedInAgent, props.dashboardId, props.projectId,
        props.routeContext, trans, uiDispatch]);



    /**
     * On 'Move and Resize widgets' button clicked, we will toggle the editing mode
     */
    const onMoveAndResizeWidgetsClick = useCallback(() => {
        setIsInMoveAndResizeMode(!isInMoveAndResizeMode);
    }, [isInMoveAndResizeMode]);



    /**
     * On Edit Dashboard button clicked, show the slide out panel for the selected dashboardId. If dashboardId is not specified,
     * treat it as creating new Dashboard.
     */
    const onEditDashboardClick = useCallback((dashboardId: number | undefined) => {
        const options: DashboardPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.DASHBOARD_PREVIEW,
            compProps: {
                data        : { projectId: props.projectId, dashboardId },
                routeContext: props.routeContext,
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.projectId, props.routeContext, uiDispatch]);



    /**
     * Alerts Dashboard widget requires Alerting feature which requires Entities feature therefore we will not enable the Alerts Dashboard
     * widget type if one of these features is not enabled.
     */
    const excludedWidgetTypes = useMemo((): SelectDashboardWidgetTypeDialogProps['excludedWidgetTypes'] => {
        const excludedWidgetTypes: DashboardWidgetType[] = [];
        if (!(entityFeatureEnabled)) {
            excludedWidgetTypes.push(DashboardWidgetType.ENTITIES_MAP);
        }
        if (!(entityFeatureEnabled && alertingFeatureEnabled)) {
            excludedWidgetTypes.push(DashboardWidgetType.ALERTS);
        }
        return excludedWidgetTypes;
    }, [alertingFeatureEnabled, entityFeatureEnabled]);


    return (
        <div
            className={clsx(classes.root, props.className)}
        >
            {selectedDashboard && (
                <div>
                    <div className={classes.header}>
                        <div className={classes.titleContainer}>
                            <div className={classes.title}>
                                {selectedDashboard.title}
                            </div>
                            <div className={classes.subtitle}>
                                {selectedDashboard.subtitle}
                            </div>
                        </div>

                        {canEditDashboard && !isInMoveAndResizeMode && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={onAnchorElClicked}
                                startIcon={<Icon>settings</Icon>}
                            >
                                {capitalizeAll(trans('dashboard.options.label'))}
                            </Button>
                        )}
                        {isInMoveAndResizeMode && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={onMoveAndResizeWidgetsClick}
                            >
                                {capitalizeAll(trans('label.done'))}
                            </Button>
                        )}

                    </div>

                    {displayableWidgets && displayableWidgets.length > 0 ? (
                        <ResponsiveGridLayout
                            className={clsx(classes.dashboardWidgetsContainer, props.className)}
                            margin={[DASHBOARD_WIDGETS_SPACING, DASHBOARD_WIDGETS_SPACING]}
                            cols={DASHBOARD_COLS}
                            breakpoints={{ lg: 0 }}
                            rowHeight={DASHBOARD_ROW_HEIGHT}
                            layouts={widgetsLayouts}
                            isResizable={isInMoveAndResizeMode}
                            isDraggable={isInMoveAndResizeMode}
                            onResizeStop={onWidgetResizedOrMoved}
                            onDragStop={onWidgetResizedOrMoved}
                            draggableHandle={`.${panelClasses.panelHeader}`}
                            draggableCancel={`.${panelClasses.panelContent} .${panelClasses.panelHeader}`}
                        >
                            {displayableWidgets.map((widget, index) => {

                                // Make sure widget data is valid first because bad widget settings can break the app.
                                if (!isDashboardWidgetSettings(widget)) {
                                    // eslint-disable-next-line no-console
                                    console.log('Invalid widget config', widget);
                                    // eslint-disable-next-line react/no-array-index-key
                                    return <div key={index.toString()} />;
                                }

                                return (
                                    <div key={widget.id.toString()}>
                                        <DashboardWidgetContainer
                                            projectId={props.projectId}
                                            dashboardId={props.dashboardId}
                                            selectedWidget={widget.id}
                                            isInMoveAndResizeMode={isInMoveAndResizeMode}
                                            enableMoveAndResizeModeHandler={() => {
                                                setIsInMoveAndResizeMode(true);
                                            }}
                                            infoCompProps={{
                                                className: clsx(classes.widget),
                                            }}
                                            routeContext={props.routeContext}
                                        />
                                    </div>
                                );
                            })}
                        </ResponsiveGridLayout>
                    ) : (
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.dashboard_widget', { count: 2 })),
                            })}
                            action={canEditDashboard ? trans('dashboard.action.add_dashboard_widget') : undefined}
                            actionHandler={() => {
                                setShowSelectDashboardWidgetTypeDialog(true);
                            }}
                        />
                    )}
                </div>
            )}

            <SelectDashboardWidgetTypeDialog
                open={showSelectDashboardWidgetTypeDialog}
                projectId={props.projectId}
                excludedWidgetTypes={excludedWidgetTypes}
                onCancel={() => {
                    setShowSelectDashboardWidgetTypeDialog(false);
                }}
                onWidgetTypeSelected={(type) => {
                    setShowSelectDashboardWidgetTypeDialog(false);
                    onCreateWidgetClick(type);
                }}
            />


            {/* Dashboard Dropdown menu */}
            <Menu
                className={classes.dropdownMenu}
                getContentAnchorEl={null}
                anchorEl={menuAnchorEl}
                keepMounted
                anchorOrigin={{
                    vertical  : 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical  : 'top',
                    horizontal: 'right',
                }}
                open={isMenuOpened}
                onClose={onMenuClosed}
            >
                <MenuItem
                    onClick={() => {
                        onMenuClosed();
                        onCreateDashboardClick();
                    }}
                >
                    <ListItemIcon>
                        <FontIcon iconName="add" />
                    </ListItemIcon>
                    {capitalizeAll(trans('dashboard.action.create_dashboard'))}
                </MenuItem>

                <MenuItem
                    className={classes.menuItem}
                    onClick={() => {
                        onMenuClosed();
                        onEditDashboardClick(props.dashboardId);
                    }}
                >
                    <ListItemIcon>
                        <FontIcon className="icon" iconName="edit" />
                    </ListItemIcon>
                    {capitalizeAll(trans('dashboard.action.edit_dashboard'))}
                </MenuItem>

                {/* If the selected Dashboard is NOT the first item, enable <- button */}
                <MenuItem
                    disabled={!dashboards || dashboards.length === 0 || selectedDashboardIndex <= 0}
                    onClick={() => {
                        onMenuClosed();
                        if (selectedDashboard) {
                            onMoveDashboardForward(selectedDashboard);
                        }
                    }}
                >
                    <ListItemIcon>
                        <FontIcon iconName="arrow_back" />
                    </ListItemIcon>
                    {capitalizeAll(trans('dashboard.action.move_dashboard_forward'))}
                </MenuItem>

                {/* If the selected Dashboard is NOT the LAST item, enable -> button */}
                <MenuItem
                    disabled={!dashboards || dashboards.length === 0 || selectedDashboardIndex >= dashboards.length - 1}
                    onClick={() => {
                        onMenuClosed();
                        if (selectedDashboard) {
                            onMoveDashboardBackward(selectedDashboard);
                        }
                    }}
                >
                    <ListItemIcon>
                        <FontIcon iconName="arrow_forward" />
                    </ListItemIcon>
                    {capitalizeAll(trans('dashboard.action.move_dashboard_backward'))}
                </MenuItem>

                <Divider />

                <MenuItem
                    className={classes.menuItem}
                    onClick={() => {
                        onMenuClosed();
                        setShowSelectDashboardWidgetTypeDialog(true);
                    }}
                >
                    <ListItemIcon>
                        <FontIcon iconName="add" />
                    </ListItemIcon>
                    {capitalizeAll(trans('dashboard.action.add_dashboard_widget'))}
                </MenuItem>

                {displayableWidgets && displayableWidgets.length > 0 && (
                    <MenuItem
                        className={classes.menuItem}
                        onClick={() => {
                            onMenuClosed();
                            onMoveAndResizeWidgetsClick();
                        }}
                    >
                        <ListItemIcon>
                            <FontIcon iconName="open_with" />
                        </ListItemIcon>
                        {capitalizeAll(trans('dashboard.action.move_and_resize_widgets'))}
                    </MenuItem>
                )}
            </Menu>
        </div>
    );
};



// Register this component with the RitzComponentsCollection so that it can be used by the Custom UI system
RitzComponentsCollection.getInstance().registerComponent('DashboardContainer', DashboardContainer);
